import { Button, Switch, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { StatusChip } from '@/components/ui/status-chip';
import { mmProgramPath } from '@/lib/paths';
import { uniqueSorted } from '@/utils/array';
import { currentOf } from '../all-programs/grid-rows';
import { reviewAndSave, type SaveFailure, SaveFailuresAlert, UnsavedEditsBar } from '../all-programs/save-programs';
import { FilterSelect } from '../all-programs/schedule-filters';
import { editableOf, type ProgramDrafts } from '../all-programs/use-program-drafts';
import { blankDowntimeRule, describeDowntimeRule } from '../mm-program-editing';
import { type DowntimeRuleInput, findSettingsProblems } from '../mm-program-schema';
import { DowntimeGroupDialog, type DowntimeGroupDraft, type DowntimeTarget } from './downtime-group-dialog';
import { type DowntimeGroup, type DowntimeRuleTerms, downtimeMarketsOf, groupDowntime, hasDowntimeEnded, putDowntimeGroup, removeDowntimeGroup } from './downtime-groups';

const ALL = 'all';

interface DowntimeRow {
  id: string;
  pid: string;
  name: string;
  canEdit: boolean;
  group: DowntimeGroup;
  /** Whether it covers every market the market maker has downtime available on */
  coversAll: boolean;
  hasEnded: boolean;
  /** Not saved as shown: new, or edited since loading */
  isUnsaved: boolean;
}

interface Editing {
  /** The rule being edited; null for a new one */
  group: DowntimeGroup | null;
  draft: DowntimeGroupDraft;
}

const groupId = (group: DowntimeGroup) => `${group.key}|${group.instruments.join(',')}`;

const withId = (terms: DowntimeRuleTerms, id = ''): DowntimeRuleInput => ({ ...terms, id }) as DowntimeRuleInput;

/** By market maker, rules still in force first, recurring before one-off, then by when they apply. */
const compareRows = (left: DowntimeRow, right: DowntimeRow) =>
  left.name.localeCompare(right.name) ||
  Number(left.hasEnded) - Number(right.hasEnded) ||
  Number(left.group.terms.type === 'one_off') - Number(right.group.terms.type === 'one_off') ||
  describeDowntimeRule(withId(left.group.terms)).localeCompare(describeDowntimeRule(withId(right.group.terms)));

interface DowntimeViewProps {
  drafts: ProgramDrafts;
  isLoading: boolean;
  onReload: (pid: string) => void;
}

/** Every market maker's downtime rules, one row per rule across the markets it covers, edited as unsaved drafts like the programs grid. */
export const DowntimeView = ({ drafts, isLoading, onReload }: DowntimeViewProps) => {
  const queryClient = useQueryClient();
  const { documents, edited } = drafts;
  const [marketMaker, setMarketMaker] = useState(ALL);
  const [market, setMarket] = useState(ALL);
  const [showEnded, setShowEnded] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [failures, setFailures] = useState<SaveFailure[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const rows = useMemo(
    () =>
      documents
        .flatMap((document): DowntimeRow[] => {
          const current = currentOf(document);
          const markets = downtimeMarketsOf(current);
          const saved = new Set(groupDowntime(document.saved).map(groupId));
          return groupDowntime(current).map((group) => ({
            id: `${document.pid}|${groupId(group)}`,
            pid: document.pid,
            name: current.name,
            canEdit: document.canEdit,
            group,
            coversAll: markets.length > 1 && markets.every((item) => group.instruments.includes(item)),
            hasEnded: hasDowntimeEnded(group.terms),
            isUnsaved: !saved.has(groupId(group)),
          }));
        })
        .sort(compareRows),
    [documents],
  );

  const targets = useMemo(
    () =>
      documents
        .filter((document) => document.canEdit)
        .map((document): DowntimeTarget => {
          const current = currentOf(document);
          return { pid: document.pid, name: current.name, active: current.active === true, markets: downtimeMarketsOf(current) };
        })
        .filter((target) => target.markets.length),
    [documents],
  );

  const marketMakerFilter = [{ value: ALL, label: 'All market makers' }, ...uniqueSorted(rows.map((row) => row.name)).map((name) => ({ value: name, label: name }))];
  const marketFilter = [{ value: ALL, label: 'All markets' }, ...uniqueSorted(rows.flatMap((row) => row.group.instruments)).map((item) => ({ value: item, label: item }))];
  const visible = rows.filter(
    (row) => (marketMaker === ALL || row.name === marketMaker) && (market === ALL || row.group.instruments.includes(market)) && (showEnded || !row.hasEnded),
  );
  const hiddenEnded = rows.filter((row) => row.hasEnded && !showEnded).length;
  const editedProblems = edited.reduce((total, document) => total + findSettingsProblems(currentOf(document)).length, 0);
  const canAdd = targets.length > 0;

  const update = (pid: string, change: (document: ReturnType<typeof editableOf>) => ReturnType<typeof editableOf>) => {
    const document = documents.find((item) => item.pid === pid);
    if (document) drafts.setDocuments(new Map([[pid, change(editableOf(document))]]));
  };

  const add = (type: DowntimeRuleInput['type']) => {
    // A new rule starts on the filtered market maker, when one is picked and editable.
    const target = targets.find((item) => item.name === marketMaker);
    const instruments = target ? target.markets.filter((item) => market === ALL || item === market) : [];
    setEditing({ group: null, draft: { pid: target?.pid ?? '', instruments, rule: blankDowntimeRule(type) } });
  };

  const edit = (row: DowntimeRow) =>
    setEditing({ group: row.group, draft: { pid: row.pid, instruments: row.group.instruments, rule: withId(row.group.terms, crypto.randomUUID()) } });

  const save = async () => {
    setIsSaving(true);
    try {
      setFailures(await reviewAndSave(edited, queryClient));
    } finally {
      setIsSaving(false);
    }
  };

  const reload = (pid: string) => {
    drafts.discard(pid);
    setFailures((previous) => previous.filter((failure) => failure.pid !== pid));
    onReload(pid);
  };

  if (isLoading && !documents.length) return <LoadingPanel className="py-24" />;

  return (
    <div className="flex flex-col gap-4">
      <SaveFailuresAlert failures={failures} onReload={reload} />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect label="Market maker" onChange={setMarketMaker} options={marketMakerFilter} value={marketMaker} />
          <FilterSelect label="Market" onChange={setMarket} options={marketFilter} value={market} />
          <span className="flex items-center gap-1.5 text-xs">
            <Switch checked={showEnded} id="downtime-show-ended" onCheckedChange={setShowEnded} />
            <label htmlFor="downtime-show-ended">Show ended{hiddenEnded ? ` (${hiddenEnded})` : ''}</label>
          </span>
        </div>
        {canAdd ? (
          <div className="flex gap-2">
            <Button className="gap-1.5" onClick={() => add('recurring')} size="sm" type="button" variant="outline">
              <Plus className="size-4" />
              Add recurring
            </Button>
            <Button className="gap-1.5" onClick={() => add('one_off')} size="sm" type="button" variant="outline">
              <Plus className="size-4" />
              Add one-off
            </Button>
          </div>
        ) : null}
      </div>

      <p className="text-xs text-muted-foreground">
        Downtime is excluded from scoring, open-volume KPI time and out-of-book. Times are UTC. A rule on several markets is stored once per market and shown here once.
      </p>

      <div className="rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Market maker</TableHead>
              <TableHead>Markets</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>When</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-40" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length ? (
              visible.map((row) => (
                <TableRow className={row.hasEnded ? 'text-muted-foreground' : undefined} key={row.id}>
                  <TableCell>
                    <Link className="font-medium underline-offset-2 hover:underline" to={mmProgramPath(row.pid)}>
                      {row.name}
                    </Link>
                  </TableCell>
                  <TableCell className="max-w-72">
                    <span className="font-mono text-xs">{row.group.instruments.join(', ')}</span>
                    {row.coversAll ? <span className="ml-1.5 text-xs text-muted-foreground">(all)</span> : null}
                  </TableCell>
                  <TableCell>{row.group.terms.type === 'recurring' ? 'Recurring' : 'One-off'}</TableCell>
                  <TableCell className="font-mono text-xs">{describeDowntimeRule(withId(row.group.terms))}</TableCell>
                  <TableCell>{row.group.terms.reason}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1.5">
                      <StatusChip size="sm" variant={row.hasEnded ? 'muted' : row.group.terms.enabled ? 'success' : 'muted'}>
                        {row.hasEnded ? 'Ended' : row.group.terms.enabled ? 'Enabled' : 'Disabled'}
                      </StatusChip>
                      {row.isUnsaved ? (
                        <StatusChip size="sm" variant="info">
                          Unsaved
                        </StatusChip>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="space-x-2 text-right">
                    {row.canEdit ? (
                      <>
                        <Button onClick={() => edit(row)} size="sm" type="button" variant="outline">
                          Edit
                        </Button>
                        <Button onClick={() => update(row.pid, (document) => removeDowntimeGroup(document, row.group))} size="sm" type="button" variant="ghost">
                          Delete
                        </Button>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell className="py-10 text-center text-muted-foreground" colSpan={7}>
                  {rows.length ? 'No downtime matches these filters.' : 'No market maker has downtime yet.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <UnsavedEditsBar isSaving={isSaving} names={edited.map((document) => currentOf(document).name)} onDiscard={() => drafts.discard()} onSave={save} problems={editedProblems} />

      {editing ? (
        <DowntimeGroupDialog
          initial={editing.draft}
          isNew={editing.group === null}
          onClose={() => setEditing(null)}
          onSave={(pid, terms, instruments) => {
            update(pid, (document) => putDowntimeGroup(document, editing.group, terms, instruments));
            setEditing(null);
          }}
          targets={targets}
        />
      ) : null}
    </div>
  );
};
DowntimeView.displayName = 'DowntimeView';
